import React, { useState, useEffect } from 'react';
import { Modal, Button, Spinner, Row, Col, Form, Badge, Alert } from 'react-bootstrap';
import { ExternalLink, Edit2, Plus, AlignLeft, Calendar, Link2, AlertTriangle, Image, Tag } from 'lucide-react';
import up_right_arrow from '../../../Images/button-icons/up_right_arrow.png';
import { FirebaseImage, ImageSize } from '../../../Helpers/ImageLoader';
import { getCoverPhotoStoragePath } from '../../../Helpers/utils';
import { placeholderForBuildingType } from '../../../Helpers/PlaceholderUtils';
import './IssueDetailModal.css';
import SourceWorkOrderBadge from './SourceWorkOrderBadge';
import IssueAssessmentBadge from './IssueAssessmentBadge';
import { getDatabase, ref, get } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import { IssueWithBuilding, IssuePriority, IssueComponentCategory, IssueComponentCategoryInput } from '../../../types/issues';
import { issueComponentCategoryText } from '../../../Helpers/issueUtils';
import { resolveBuildComponentTypeTranslation } from '../../../Helpers/BuildComponentTypesStore';
import ComponentCategoryPicker from './ComponentCategoryPicker';
import { issuePriorityColors, issuePriorityLabels } from '../../../types/issuePriorityDisplay';
import { EditablePriorityBadge, PriorityOption } from '../../../Components/EditablePriorityBadge';
import { BaseComponent, Building, BuildingModel, BuildingStructure, BuildingSummary, BuildingInfo } from '../../../types/protocols';
import { BaseComponentImpl } from '../../../types/implementations';
import { useUsersData } from '../../../Helpers/UsersContext';
import { translate } from '../../../types/EnumTranslations';
import { useUserNames } from '../../../Helpers/useUserNames';
import { issuesApi } from '../../../api/issuesApi';
import PhotoGallery from '../../../Components/PhotoGallery';
import MiniMap from '../../../Components/MiniMap';
import ComponentPickerModal from './ComponentPickerModal';
import LinkedComponentCard from '../../../Components/LinkedComponentCard';
import { SnoozeModal } from '../../../Components/SnoozeModal';
import ConfirmationModal from '../../../Components/ConfirmationModal';
import { applyComponentPatch } from '../../../Helpers/componentPatch';
import { useIssueAutoAssignment } from '../../../hooks/issues/useIssueAutoAssignment';
import { issueAssessmentState } from '../../../Helpers/issueAssessment';

interface IssueDetailModalProps {
    show: boolean;
    onHide: () => void;
    issue: IssueWithBuilding;
    // Receives the issue's live component link and priority so just-made edits
    // are carried into the work order without waiting for the issue list to refetch.
    // `componentLabel` (the component's name or the category) prefills the work order's title.
    onConvert: (latest: { componentKey?: string; componentCategory?: IssueComponentCategory; priority: IssuePriority; componentLabel?: string }) => void;
    onArchive?: (reason?: string) => Promise<void>;
    /** Called after an in-modal edit (component link or priority) so the parent
     *  can refresh the issue list. */
    onIssueChanged?: () => void;
    onSnooze?: (snoozeDate: string) => Promise<void>;
    onUnsnooze?: () => Promise<void>;
}

const ISSUE_PRIORITY_OPTIONS: PriorityOption<IssuePriority>[] = [
    IssuePriority.HIGH,
    IssuePriority.MEDIUM,
    IssuePriority.LOW,
].map(p => ({ value: p, label: issuePriorityLabels[p], variant: issuePriorityColors[p] }));

/**
 * Minimal building data needed for component display
 * Optimized to avoid fetching full building model
 */
interface MinimalBuildingData {
    key: string;
    name: string;
    structure?: {
        components?: { [key: string]: BaseComponent };
        rooms?: { [key: string]: any };
        levels?: { [key: string]: any };
        roofPlan?: BuildingStructure['roofPlan'];
    };
    mapImages?: { [key: string]: any };
    currentMapImageKey?: string;
}

interface ComponentWithBuilding {
    component: BaseComponentImpl;
    building: Building;
    minimalBuilding: MinimalBuildingData;
}

/**
 * Modal showing issue details with photos and component location data
 */
const IssueDetailModal: React.FC<IssueDetailModalProps> = ({
    show,
    onHide,
    issue,
    onConvert,
    onArchive,
    onIssueChanged,
    onSnooze,
    onUnsnooze
}) => {
    const [componentData, setComponentData] = useState<ComponentWithBuilding | null>(null);
    const [loadingComponent, setLoadingComponent] = useState(false);
    const [reporterName, setReporterName] = useState<string | null>(null);
    const [showComponentPicker, setShowComponentPicker] = useState(false);
    const [savingComponent, setSavingComponent] = useState(false);
    const [fullBuilding, setFullBuilding] = useState<Building | null>(null);
    const [loadingFullBuilding, setLoadingFullBuilding] = useState(false);
    const [buildingSummary, setBuildingSummary] = useState<BuildingSummary | null>(null);
    // Archive confirmation state
    const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
    const [archiveReason, setArchiveReason] = useState('');
    const [archiving, setArchiving] = useState(false);
    // Asks before an admin acts on an issue still waiting for the förvaltare.
    const [pendingAction, setPendingAction] = useState<'convert' | 'archive' | null>(null);
    const { setting: autoAssignment } = useIssueAutoAssignment();
    // Snooze state
    const [showSnoozeModal, setShowSnoozeModal] = useState(false);
    const [unsnoozing, setUnsnoozing] = useState(false);
    // Track the current componentKey locally to handle updates without waiting for parent refresh
    const [currentComponentKey, setCurrentComponentKey] = useState<string | undefined>(issue.componentKey);
    // The category alternative to a linked component (mutually exclusive with it).
    const [currentCategory, setCurrentCategory] = useState<IssueComponentCategory | undefined>(issue.componentCategory);
    // "Kategorisera efter typ" checkbox. Checked whenever a category is set;
    // may also be checked with nothing chosen yet (the picker is then shown empty).
    const [categoryMode, setCategoryMode] = useState<boolean>(!!issue.componentCategory);
    const [savingCategory, setSavingCategory] = useState(false);
    // Asks before a linked component is swapped for its type.
    const [showCategorySwapConfirm, setShowCategorySwapConfirm] = useState(false);
    // Error from an in-modal edit (component link, category, priority), shown inline.
    const [editError, setEditError] = useState<string | null>(null);
    // Likewise track priority locally so the badge reflects an edit immediately.
    const [currentPriority, setCurrentPriority] = useState<IssuePriority>(issue.priority);
    const { getUserName } = useUserNames();
    const { users } = useUsersData();
    const auth = getAuth();

    // Check if current user is admin
    const isAdmin = users?.find(u => u.key === auth.currentUser?.uid)?.admin || false;

    // Sync local componentKey with issue prop when it changes
    useEffect(() => {
        setCurrentComponentKey(issue.componentKey);
    }, [issue.componentKey]);

    useEffect(() => {
        setCurrentCategory(issue.componentCategory);
        setCategoryMode(!!issue.componentCategory);
    }, [issue.componentCategory]);

    // Sync local priority with issue prop when it changes
    useEffect(() => {
        setCurrentPriority(issue.priority);
    }, [issue.priority]);

    // Fetch reporter name
    useEffect(() => {
        if (show && issue.createdBy) {
            getUserName(issue.createdBy).then(setReporterName);
        }
    }, [show, issue.createdBy, getUserName]);

    // Fetch building summary for map display
    useEffect(() => {
        if (!show || !issue.buildingKey) {
            setBuildingSummary(null);
            return;
        }

        const fetchBuildingSummary = async () => {
            try {
                const db = getDatabase();
                const summaryRef = ref(db, `buildingsSummary/${issue.buildingKey}`);
                const snapshot = await get(summaryRef);
                if (snapshot.exists()) {
                    setBuildingSummary(snapshot.val() as BuildingSummary);
                }
            } catch (error) {
                console.error('Error fetching building summary:', error);
            }
        };

        fetchBuildingSummary();
    }, [show, issue.buildingKey]);

    // Fetch component and building data if issue has componentKey
    useEffect(() => {
        if (show && currentComponentKey && issue.buildingKey) {
            fetchComponentAndBuilding(currentComponentKey);
        } else if (!currentComponentKey) {
            setComponentData(null);
        }
    }, [show, currentComponentKey, issue.buildingKey]);

    const fetchComponentAndBuilding = async (componentKey: string) => {
        if (!componentKey || !issue.buildingKey) return;

        setLoadingComponent(true);
        try {
            const db = getDatabase();
            const buildingPath = `buildings/${issue.buildingKey}`;

            // Fetch only what we need instead of the full building model
            // This avoids loading all documents, photos, todos, etc.
            const [
                summarySnapshot,
                componentSnapshot,
                componentsSnapshot,
                roomsSnapshot,
                levelsSnapshot,
                roofPlanSnapshot,
                mapImagesSnapshot,
                currentMapKeySnapshot
            ] = await Promise.all([
                get(ref(db, `buildingsSummary/${issue.buildingKey}`)),
                get(ref(db, `${buildingPath}/structure/components/${componentKey}`)),
                get(ref(db, `${buildingPath}/structure/components`)),
                get(ref(db, `${buildingPath}/structure/rooms`)),
                get(ref(db, `${buildingPath}/structure/levels`)),
                get(ref(db, `${buildingPath}/structure/roofPlan`)),
                get(ref(db, `${buildingPath}/mapImages`)),
                get(ref(db, `${buildingPath}/currentMapImageKey`))
            ]);

            if (summarySnapshot.exists() && componentSnapshot.exists()) {
                const summary = summarySnapshot.val() as BuildingSummary;
                const componentRaw = componentSnapshot.val() as BaseComponent;

                // Build minimal data structures
                const minimalBuilding: MinimalBuildingData = {
                    key: issue.buildingKey,
                    name: summary.name || 'Okänd byggnad',
                    structure: {
                        components: componentsSnapshot.exists() ? componentsSnapshot.val() : undefined,
                        rooms: roomsSnapshot.exists() ? roomsSnapshot.val() : undefined,
                        levels: levelsSnapshot.exists() ? levelsSnapshot.val() : undefined,
                        roofPlan: roofPlanSnapshot.exists() ? roofPlanSnapshot.val() : undefined
                    },
                    mapImages: mapImagesSnapshot.exists() ? mapImagesSnapshot.val() : undefined,
                    currentMapImageKey: currentMapKeySnapshot.exists() ? currentMapKeySnapshot.val() : undefined
                };

                // Build a Building object for components that need it
                // This is a partial model - only includes structure and map data
                const building: Building = {
                    key: issue.buildingKey,
                    model: {
                        structure: minimalBuilding.structure,
                        mapImages: minimalBuilding.mapImages,
                        currentMapImageKey: minimalBuilding.currentMapImageKey
                    } as BuildingModel,
                    summary
                };

                const component = new BaseComponentImpl(componentRaw);

                setComponentData({ component, building, minimalBuilding });
            }
        } catch (error) {
            console.error('Error fetching component data:', error);
        } finally {
            setLoadingComponent(false);
        }
    };

    // Fetch full building data for component picker
    const fetchFullBuilding = async () => {
        if (!issue.buildingKey) return;

        setLoadingFullBuilding(true);
        try {
            const db = getDatabase();
            const buildingPath = `buildings/${issue.buildingKey}`;

            const [summarySnapshot, structureSnapshot, mapImagesSnapshot, currentMapKeySnapshot] = await Promise.all([
                get(ref(db, `buildingsSummary/${issue.buildingKey}`)),
                get(ref(db, `${buildingPath}/structure`)),
                get(ref(db, `${buildingPath}/mapImages`)),
                get(ref(db, `${buildingPath}/currentMapImageKey`))
            ]);

            if (summarySnapshot.exists()) {
                const summary = summarySnapshot.val() as BuildingSummary;

                const building: Building = {
                    key: issue.buildingKey,
                    model: {
                        structure: structureSnapshot.exists() ? structureSnapshot.val() : undefined,
                        mapImages: mapImagesSnapshot.exists() ? mapImagesSnapshot.val() : undefined,
                        currentMapImageKey: currentMapKeySnapshot.exists() ? currentMapKeySnapshot.val() : undefined
                    } as BuildingModel,
                    summary
                };

                setFullBuilding(building);
            }
        } catch (error) {
            console.error('Error fetching full building:', error);
        } finally {
            setLoadingFullBuilding(false);
        }
    };

    // Handle opening the component picker
    const handleEditComponent = async () => {
        if (!fullBuilding) {
            // Reuse building data from fetchComponentAndBuilding if available
            if (componentData?.building) {
                setFullBuilding(componentData.building);
            } else {
                await fetchFullBuilding();
            }
        }
        setShowComponentPicker(true);
    };

    // Handle archive confirmation
    const handleArchive = async () => {
        if (!onArchive) return;

        setArchiving(true);
        try {
            await onArchive(archiveReason.trim() || undefined);
            setShowArchiveConfirm(false);
            setArchiveReason('');
        } catch (error) {
            // Error handled by parent, just stop spinner
        } finally {
            setArchiving(false);
        }
    };

    // Reset archive state when modal closes
    const handleHide = () => {
        setShowArchiveConfirm(false);
        setArchiveReason('');
        setShowSnoozeModal(false);
        onHide();
    };

    // Handle snooze
    const handleSnooze = async (snoozeDate: string) => {
        if (!onSnooze) return;
        await onSnooze(snoozeDate);
        setShowSnoozeModal(false);
    };

    // Handle unsnooze
    const handleUnsnooze = async () => {
        if (!onUnsnooze) return;
        setUnsnoozing(true);
        try {
            await onUnsnooze();
        } finally {
            setUnsnoozing(false);
        }
    };

    // Handle component selection from the picker. Linking a component clears
    // any category (the two are mutually exclusive); clearing leaves neither.
    const handleComponentSelect = async (componentKey: string | null) => {
        setSavingComponent(true);
        try {
            await issuesApi.updateComponentLink(issue.key, componentKey ? { componentKey } : null);
            setShowComponentPicker(false);
            // Update local state to trigger immediate UI refresh
            setCurrentComponentKey(componentKey || undefined);
            setCurrentCategory(undefined);
            setCategoryMode(false);
            // Notify parent to refresh the issue data
            onIssueChanged?.();
        } catch (error) {
            console.error('Error updating component:', error);
            setEditError(error instanceof Error ? error.message : 'Kunde inte uppdatera komponent');
        } finally {
            setSavingComponent(false);
        }
    };

    // Save a category (replacing any linked component) or clear it.
    const saveCategory = async (input: IssueComponentCategoryInput | null) => {
        setSavingCategory(true);
        try {
            await issuesApi.updateComponentLink(issue.key, input ? { componentCategory: input } : null);
            setCurrentComponentKey(undefined);
            setCurrentCategory(input
                ? { ...input, label: resolveBuildComponentTypeTranslation(input.specificType) || translate(input.baseType) }
                : undefined);
            onIssueChanged?.();
        } catch (error) {
            console.error('Error updating component category:', error);
            setEditError(error instanceof Error ? error.message : 'Kunde inte uppdatera kategori');
        } finally {
            setSavingCategory(false);
        }
    };

    const handleCategoryModeToggle = async (checked: boolean) => {
        if (!checked) {
            setCategoryMode(false);
            if (currentCategory) {
                await saveCategory(null);
            }
            return;
        }
        if (currentComponentKey) {
            // Swapping a real component for its type needs a deliberate choice.
            setShowCategorySwapConfirm(true);
            return;
        }
        setCategoryMode(true);
    };

    // Confirmed: drop the component link and seed the category from its type
    // so nothing is lost.
    const handleCategorySwapConfirm = async () => {
        setShowCategorySwapConfirm(false);
        setCategoryMode(true);
        await saveCategory(component?.type
            ? { baseType: component.type, specificType: component.specificType?.value || undefined }
            : null);
    };


    // Handle priority change from the priority badge
    const handlePrioritySelect = async (priority: IssuePriority) => {
        try {
            await issuesApi.updatePriority(issue.key, priority);
            // Reflect the change immediately and notify the parent to refresh.
            setCurrentPriority(priority);
            onIssueChanged?.();
        } catch (error) {
            console.error('Error updating priority:', error);
            setEditError(error instanceof Error ? error.message : 'Kunde inte uppdatera prioritet');
        }
    };

    // Format date with time
    const formatDateTime = (dateStr: string) => {
        // Parse YYYY-MM-DD hh:mm:ss format
        const [datePart, timePart] = dateStr.split(' ');
        const date = new Date(datePart);
        const formattedDate = date.toLocaleDateString('sv-SE');

        if (timePart) {
            // Show time without seconds
            const [hours, minutes] = timePart.split(':');
            return `${formattedDate} kl ${hours}:${minutes}`;
        }
        return formattedDate;
    };

    const hasPhotos = issue.photos && Object.keys(issue.photos).length > 0;
    const component = componentData?.component;
    const building = componentData?.building;
    const effectiveSummary = buildingSummary || building?.summary;
    const linkedComponentName = component && building ? component.displayName(building) : 'komponenten';
    const isPendingForvaltare = issueAssessmentState(issue, autoAssignment) === 'pending';

    const convertNow = () => onConvert({
        componentKey: currentComponentKey,
        componentCategory: currentCategory,
        priority: currentPriority,
        componentLabel: currentComponentKey
            ? (component && building ? component.displayName(building) : undefined)
            : (currentCategory ? issueComponentCategoryText(currentCategory) : undefined),
    });
    const startConvert = () => (isPendingForvaltare ? setPendingAction('convert') : convertNow());
    const startArchive = () => (isPendingForvaltare ? setPendingAction('archive') : setShowArchiveConfirm(true));
    const confirmPendingAction = () => {
        const action = pendingAction;
        setPendingAction(null);
        if (action === 'convert') convertNow();
        if (action === 'archive') setShowArchiveConfirm(true);
    };

    const componentPageUrl = currentComponentKey
        ? `/building/${issue.buildingKey}/component/${currentComponentKey}`
        : null;

    // Address formatting helpers - prefer buildingSummary, fallback to component building
    const effectiveBuildingInfo = buildingSummary?.buildingInfo || building?.summary?.buildingInfo;
    const effectiveCoordinates = buildingSummary?.coordinates || building?.summary?.coordinates;
    const formatAddress = (info: BuildingInfo | undefined): string | null => {
        if (!info) return null;
        const parts: string[] = [];
        if (info.street) parts.push(info.street);
        const cityParts: string[] = [];
        if (info.postalCode) cityParts.push(info.postalCode);
        if (info.city) cityParts.push(info.city);
        if (cityParts.length > 0) parts.push(cityParts.join(' '));
        return parts.length > 0 ? parts.join(', ') : null;
    };
    const address = formatAddress(effectiveBuildingInfo);
    const hasCoordinates = effectiveCoordinates?.latitude && effectiveCoordinates?.longitude;
    const googleMapsUrl = hasCoordinates
        ? `https://maps.google.com/?q=${effectiveCoordinates.latitude},${effectiveCoordinates.longitude}`
        : address
            ? `https://maps.google.com/search/${encodeURIComponent(address)}`
            : null;

    return (
        <Modal show={show} onHide={handleHide} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title className="d-flex align-items-center gap-2">
                    <Badge bg="" className="badge-felanmalan d-inline-flex align-items-center gap-1">
                        <AlertTriangle size={10} />
                        Felanmälan
                    </Badge>
                    <EditablePriorityBadge
                        value={currentPriority}
                        options={ISSUE_PRIORITY_OPTIONS}
                        canEdit={isAdmin}
                        onChange={handlePrioritySelect}
                        className="ms-2 priority-badge"
                    />
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {editError && (
                    <Alert variant="danger" dismissible onClose={() => setEditError(null)}>
                        {editError}
                    </Alert>
                )}

                {/* Two-column top section */}
                <Row className="mb-4">
                    {/* Left: description + reported */}
                    <Col md={6} className="mb-3 mb-md-0">
                        <h6 className="issue-modal__section-title">
                            <AlignLeft size={16} className="text-muted" />
                            Beskrivning
                        </h6>
                        <p style={{ whiteSpace: 'pre-wrap' }} className="mb-4">{issue.description}</p>

                        <h6 className="issue-modal__section-title">
                            <Calendar size={16} className="text-muted" />
                            Rapporterad
                        </h6>
                        <div>
                            {formatDateTime(issue.createdAt)}
                            {reporterName && <> av {reporterName}</>}
                        </div>
                        {issue.source === 'workOrder' && issue.sourceWorkOrderKey && (
                            <div className="mt-1"><SourceWorkOrderBadge workOrderKey={issue.sourceWorkOrderKey} /></div>
                        )}
                        <IssueAssessmentBadge issue={issue} showDetails className="mt-2" />
                    </Col>

                    {/* Right: building card + map + Google Maps button */}
                    <Col md={6}>
                        <div className="issue-modal__building-card">
                        <div className="d-flex align-items-center gap-3 mb-2">
                            <FirebaseImage
                                storagePath={effectiveSummary ? getCoverPhotoStoragePath(effectiveSummary) : undefined}
                                size={ImageSize.THUMBNAIL_SMALL}
                                placeholder={placeholderForBuildingType(effectiveSummary?.buildingType)}
                                style={{
                                    width: 80,
                                    height: 80,
                                    objectFit: 'cover',
                                    borderRadius: 6,
                                    flexShrink: 0,
                                }}
                                alt={issue.buildingName}
                            />
                            <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                <strong>{issue.buildingName}</strong>
                                {effectiveBuildingInfo?.street && (
                                    <div className="small text-muted mt-1">{effectiveBuildingInfo.street}</div>
                                )}
                                {(effectiveBuildingInfo?.postalCode || effectiveBuildingInfo?.city) && (
                                    <div className="small text-muted">
                                        {[effectiveBuildingInfo?.postalCode, effectiveBuildingInfo?.city].filter(Boolean).join(' ')}
                                    </div>
                                )}
                            </div>
                            {issue.buildingKey && buildingSummary && (
                                <a
                                    href={`/building/${issue.buildingKey}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="issue-modal__building-link-btn flex-shrink-0"
                                >
                                    <ExternalLink size={16} />
                                </a>
                            )}
                        </div>

                        {hasCoordinates && (
                            <div className="mt-3">
                                <MiniMap
                                    coordinates={effectiveCoordinates!}
                                    buildingName={issue.buildingName}
                                    height={160}
                                />
                            </div>
                        )}

                        {googleMapsUrl && (
                            <Button
                                variant="secondary"
                                className="mt-2"
                                style={{
                                    fontSize: '14px',
                                    backgroundColor: '#001D60',
                                    borderRadius: '14px',
                                    padding: '2px 40px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: 'none',
                                }}
                                onClick={() => window.open(googleMapsUrl, '_blank', 'noopener,noreferrer')}
                            >
                                Google Maps
                                <img
                                    src={up_right_arrow}
                                    alt=""
                                    style={{ width: 14, height: 14, marginLeft: 3, filter: 'brightness(0) invert(1)' }}
                                />
                            </Button>
                        )}
                        </div>
                    </Col>
                </Row>

                {/* Photo Gallery */}
                {hasPhotos && (
                    <div className="mb-4">
                        <h6 className="issue-modal__section-title mb-3">
                            <Image size={16} className="text-muted" />
                            Bilder ({Object.keys(issue.photos!).length})
                        </h6>
                        <PhotoGallery
                            photos={issue.photos!}
                            buildingKey={issue.buildingKey}
                            thumbnailSize={100}
                            showDate={true}
                        />
                    </div>
                )}

                {/* Linked Component Section */}
                {(currentComponentKey || currentCategory || isAdmin) && (
                    <div className="mb-4 mt-2">
                        <div className="d-flex align-items-center justify-content-between mb-3">
                            <h6 className="issue-modal__section-title mb-0">
                                <Link2 size={16} className="text-muted" />
                                Kopplad komponent
                            </h6>
                            {isAdmin && (
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={handleEditComponent}
                                    disabled={loadingFullBuilding}
                                >
                                    {loadingFullBuilding ? (
                                        <Spinner animation="border" size="sm" />
                                    ) : currentComponentKey ? (
                                        <>
                                            <Edit2 size={14} className="me-1" />
                                            Ändra
                                        </>
                                    ) : currentCategory ? (
                                        <>
                                            <Link2 size={14} className="me-1" />
                                            Koppla komponent
                                        </>
                                    ) : (
                                        <>
                                            <Plus size={14} className="me-1" />
                                            Lägg till
                                        </>
                                    )}
                                </Button>
                            )}
                        </div>

                        {loadingComponent ? (
                            <div className="text-center py-3">
                                <Spinner animation="border" size="sm" />
                                <span className="ms-2">Laddar komponentdata...</span>
                            </div>
                        ) : componentData ? (
                            <LinkedComponentCard
                                component={componentData.component}
                                building={componentData.building}
                                href={componentPageUrl}
                            />
                        ) : currentComponentKey ? (
                            <div className="text-muted small bg-light rounded p-3">
                                Kunde inte ladda komponentdata
                            </div>
                        ) : currentCategory && !isAdmin ? (
                            <div className="bg-light rounded p-3 d-flex align-items-center gap-2">
                                <Tag size={14} className="text-muted" />
                                <span className="fw-medium">{issueComponentCategoryText(currentCategory)}</span>
                            </div>
                        ) : null}

                        {/* Category instead of a component (admin): the box is checked
                            whenever a category is set; with a component linked, checking
                            it swaps the link for the component's type. */}
                        {!currentComponentKey && isAdmin && (
                            <div className="bg-light rounded p-3">
                                {!categoryMode && (
                                    <div className="text-muted small mb-2">Ingen komponent kopplad</div>
                                )}
                                <Form.Check
                                    type="checkbox"
                                    id="issue-category-mode"
                                    label="Ingen specifik komponent – kategorisera efter typ"
                                    checked={categoryMode}
                                    disabled={savingCategory}
                                    onChange={(e) => handleCategoryModeToggle(e.target.checked)}
                                />
                                {categoryMode && (
                                    <div className="mt-2">
                                        <ComponentCategoryPicker
                                            value={currentCategory ?? null}
                                            onChange={saveCategory}
                                            disabled={savingCategory}
                                        />
                                        {currentCategory && (
                                            <div className="small text-muted mt-2 d-flex align-items-center gap-1">
                                                <Tag size={12} />
                                                {issueComponentCategoryText(currentCategory)}
                                                {savingCategory && <Spinner animation="border" size="sm" className="ms-1" />}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                        {currentComponentKey && isAdmin && (
                            <Form.Check
                                type="checkbox"
                                id="issue-category-mode"
                                className="mt-2 small"
                                label="Ingen specifik komponent – kategorisera efter typ"
                                checked={false}
                                disabled={savingCategory || loadingComponent}
                                onChange={(e) => handleCategoryModeToggle(e.target.checked)}
                            />
                        )}
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                {isAdmin && onArchive && (
                    <Button
                        variant="outline-secondary"
                        onClick={startArchive}
                        className="me-auto"
                    >
                        Arkivera utan åtgärd
                    </Button>
                )}
                {isAdmin && onSnooze && !issue.snoozedUntil && (
                    <Button
                        variant="outline-secondary"
                        onClick={() => setShowSnoozeModal(true)}
                    >
                        Snooza
                    </Button>
                )}
                {isAdmin && onUnsnooze && issue.snoozedUntil && (
                    <Button
                        variant="outline-warning"
                        onClick={handleUnsnooze}
                        disabled={unsnoozing}
                    >
                        {unsnoozing ? 'Avsnoozar...' : 'Avsnooza'}
                    </Button>
                )}
                <Button variant="primary" onClick={startConvert}>
                    Skapa ärende
                </Button>
            </Modal.Footer>

            {/* Archive Confirmation Modal */}
            <Modal
                show={showArchiveConfirm}
                onHide={() => setShowArchiveConfirm(false)}
                centered
                size="sm"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Arkivera felanmälan</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Vill du arkivera denna felanmälan utan att skapa ett ärende?</p>
                    <Form.Group>
                        <Form.Label>Anledning (valfritt)</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={2}
                            value={archiveReason}
                            onChange={(e) => setArchiveReason(e.target.value)}
                            placeholder="T.ex. dublett, redan åtgärdat, inte vårt ansvar..."
                            maxLength={500}
                        />
                        <small className={`d-block mt-1 text-end ${archiveReason.length >= 500 ? 'text-danger' : 'text-muted'}`}>
                            {archiveReason.length} / 500
                        </small>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setShowArchiveConfirm(false)}
                        disabled={archiving}
                    >
                        Avbryt
                    </Button>
                    <Button
                        variant="danger"
                        onClick={handleArchive}
                        disabled={archiving}
                    >
                        {archiving ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Arkiverar...
                            </>
                        ) : (
                            'Arkivera utan åtgärd'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Component Picker Modal */}
            {fullBuilding && (
                <ComponentPickerModal
                    show={showComponentPicker}
                    onHide={() => setShowComponentPicker(false)}
                    building={fullBuilding}
                    currentComponentKey={currentComponentKey}
                    onSelect={handleComponentSelect}
                    saving={savingComponent}
                    onComponentPatched={(componentKey, patch) =>
                        setFullBuilding(prev => applyComponentPatch(prev, componentKey, patch))}
                    // With a category set, offer only components of that type so
                    // upgrading to a real component is a short list.
                    filter={currentCategory ? (c) =>
                        c.type === currentCategory.baseType
                        && (!currentCategory.specificType || c.specificType?.value === currentCategory.specificType)
                        : undefined}
                    hint={currentCategory ? `Visar komponenter av typen ${issueComponentCategoryText(currentCategory)}` : undefined}
                />
            )}

            {/* The förvaltare has not judged it yet: the admin may still act (ios#1342) */}
            <ConfirmationModal
                show={pendingAction !== null}
                title="Väntar på förvaltare"
                message="Förvaltaren har inte bedömt den här felanmälan än. Vill du hantera den själv?"
                confirmText={pendingAction === 'archive' ? 'Fortsätt till arkivering' : 'Skapa ärende'}
                variant="primary"
                onConfirm={confirmPendingAction}
                onCancel={() => setPendingAction(null)}
            />

            {/* Swap a linked component for its type */}
            <ConfirmationModal
                show={showCategorySwapConfirm}
                title="Kategorisera efter typ"
                message={`Ta bort kopplingen till ${linkedComponentName} och kategorisera efter typ istället? Kategorin fylls i från komponentens typ.`}
                confirmText="Kategorisera efter typ"
                variant="primary"
                onConfirm={handleCategorySwapConfirm}
                onCancel={() => setShowCategorySwapConfirm(false)}
                loading={savingCategory}
            />

            {/* Snooze Modal */}
            <SnoozeModal
                show={showSnoozeModal}
                onHide={() => setShowSnoozeModal(false)}
                itemName="Felanmälan"
                onSnooze={handleSnooze}
            />
        </Modal>
    );
};

export default IssueDetailModal;
